import { type FC, type ReactElement, type ReactNode } from "react";

import { Colors, PopoverPlacementTypes, Skeleton, Tooltip } from "@snapchat/snap-design-system";
import { ChevronDownIcon, type IconProps, WarningIcon } from "@snapchat/snap-design-system-icons";

import { Flexbox } from "../../../shared/components";
import { useHoveredState } from "../../../shared/hooks";

import { zIndex } from "./LensDestinationsTooltip.styles";
import {
    Circle,
    LensStatusContainer,
    LensStatusTagline,
    LensStatusTitle,
    WarningSectionWrapper,
    chevronButton,
    iconSpacing,
} from "./LensStatusWithDetails.styles";
import { LensDestinationTooltipTitle } from "./messages.generated";

interface LensStatusWithDetailsProps {
    icon?: (props: IconProps) => ReactElement;
    color: Colors.ColorPalette;
    isColorInverted?: boolean;
    title: string;
    tagLine?: string;
    loading: boolean;
    tooltipContent?: ReactNode;
    width?: string;
    hasDistributionRestrictions?: boolean;
    isSpectaclesOnly?: boolean;
}

export const LensStatusWithDetails: FC<LensStatusWithDetailsProps> = ({
    isColorInverted = false,
    icon: Icon,
    title,
    tagLine,
    color,
    loading,
    tooltipContent,
    width,
    hasDistributionRestrictions,
    isSpectaclesOnly = false,
}) => {
    const circleColor = isColorInverted ? Colors.WHITE : color;
    const iconColor = isColorInverted ? color : Colors.WHITE;
    const borderColor = isColorInverted ? iconColor : circleColor;

    const { isHovered, handleMouseEnter, handleMouseLeave } = useHoveredState();

    return (
        <LensStatusContainer width={width}>
            {loading ? (
                <Skeleton height={44} />
            ) : (
                <>
                    {Icon && (
                        <Circle
                            color={circleColor}
                            borderColor={borderColor}
                            className={iconSpacing}
                            data-testid="status-icon"
                        >
                            <Icon fill={iconColor} />
                        </Circle>
                    )}
                    <Flexbox flexDirection="column">
                        <LensStatusTitle data-testid="status-title">{title}</LensStatusTitle>
                        {tagLine && (
                            <Flexbox
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                                alignItems="center"
                            >
                                <LensStatusTagline data-testid="status-tagline">{tagLine}</LensStatusTagline>
                                {hasDistributionRestrictions && (
                                    <WarningSectionWrapper>
                                        <WarningIcon fill={Colors.Orange.V200} />
                                    </WarningSectionWrapper>
                                )}
                                {!isSpectaclesOnly && (
                                    <>
                                        <ChevronDownIcon className={chevronButton} />
                                        {isHovered && (
                                            <Tooltip
                                                className={zIndex}
                                                title={<LensDestinationTooltipTitle />}
                                                arrowPointAtCenter={true}
                                                visible={true}
                                                placement={PopoverPlacementTypes.BOTTOM}
                                                content={tooltipContent}
                                            />
                                        )}
                                    </>
                                )}
                            </Flexbox>
                        )}
                    </Flexbox>
                </>
            )}
        </LensStatusContainer>
    );
};
